import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="stripe-checkout"
// Loads Stripe.js dynamically and mounts the Payment Element
export default class extends Controller {
  static values = {
    publishableKey: String,
    clientSecret: String,
    primaryColor: { type: String, default: "#0f8b8d" },
    processingText: { type: String, default: "Processing..." },
    completeText: { type: String, default: "Complete Purchase" },
    successUrl: String
  }

  static targets = ["paymentElement", "errors", "form", "submitButton", "emailInput"]

  connect() {
    this.loadStripe()
  }

  async loadStripe() {
    if (typeof Stripe !== "undefined") {
      this.initializeStripe()
      return
    }

    const script = document.createElement("script")
    script.src = "https://js.stripe.com/v3/"
    script.onload = () => this.initializeStripe()
    document.head.appendChild(script)
  }

  initializeStripe() {
    this.stripe = Stripe(this.publishableKeyValue)

    const appearance = {
      theme: "stripe",
      variables: {
        colorPrimary: this.primaryColorValue,
        colorBackground: "#ffffff",
        colorText: "#1f2937",
        colorDanger: "#ef4444",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        spacingUnit: "4px",
        borderRadius: "8px"
      }
    }

    this.elements = this.stripe.elements({
      clientSecret: this.clientSecretValue,
      appearance: appearance
    })

    this.paymentElement = this.elements.create("payment", {
      layout: {
        type: "tabs",
        defaultCollapsed: false,
        radios: false,
        spacedAccordionItems: true
      },
      wallets: {
        applePay: "auto",
        googlePay: "auto"
      },
      fields: {
        billingDetails: {
          email: "never"
        }
      }
    })

    this.paymentElement.mount(this.paymentElementTarget)

    if (this.hasEmailInputTarget) {
      this.emailInputTarget.addEventListener("blur", () => {
        this.elements.update({
          billingDetails: { email: this.emailInputTarget.value }
        })
      })
    }

    this.paymentElement.on("change", (event) => {
      if (event.error) {
        this.showError(event.error.message)
      } else {
        this.hideError()
      }
    })

    this.paymentElement.on("ready", () => {
      const loading = this.paymentElementTarget.querySelector("[data-loading]")
      if (loading) loading.remove()
    })
  }

  async submit(event) {
    event.preventDefault()

    const submitButton = this.submitButtonTarget
    submitButton.disabled = true
    submitButton.value = this.processingTextValue

    const formData = new FormData(this.formTarget)
    const email = formData.get("purchase[email]")
    const firstName = formData.get("purchase[first_name]")
    const lastName = formData.get("purchase[last_name]")

    const { error } = await this.stripe.confirmPayment({
      elements: this.elements,
      confirmParams: {
        return_url: this.successUrlValue,
        receipt_email: email,
        payment_method_data: {
          billing_details: {
            name: firstName + " " + lastName,
            email: email
          }
        }
      }
    })

    if (error) {
      this.showError(error.message)
      submitButton.disabled = false
      submitButton.value = this.completeTextValue
    }
    // If successful, Stripe redirects to return_url
  }

  showError(message) {
    if (!this.hasErrorsTarget) return
    const errorMessage = this.errorsTarget.querySelector("p")
    errorMessage.textContent = message
    this.errorsTarget.classList.remove("hidden")
    this.errorsTarget.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  hideError() {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.classList.add("hidden")
  }
}
